import { Typography } from '@material-ui/core';
import * as React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import qs from 'query-string';
import IconDialog from '../../components/IconDialog/IconDialog';
import { session, simple } from '../../utils/auth';
import eventsTracking from '../../utils/eventsTracking';
import { initialRoute } from '../../utils/initialRoute';
import { useLocalAdminInfo } from '../../utils/useLocalAdminInfo';
import { useLogout } from '../../utils/useLogout';
import { loadingMessage } from '../../utils/loadingMessage';
import { parseBankString, parseBrandString } from '../../dictionaries/context/useBankFromQueryParam';
import { useLocalFromNegotiator } from '../../utils/useLocalFromNegotiator';
import { useGetProfile } from '../../utils/useGetProfile';

interface SimpleProps {}

const Simple: React.FC<SimpleProps> = () => {
  const history = useHistory();
  const { document: rawDocument } = useParams<{ document: string }>();
  const { data, refetch, loading } = useGetProfile(false);
  const loadedDocument = data?.profile?.document;
  const userId = data?.profile?.user.id;
  const logout = useLogout();
  const [ adminInfo, setAdminInfo ] = useLocalAdminInfo();
  const { search } = useLocation();
  const {
    adminToken, brand: queryBrand, bank: queryBank, outDebtId,
    fromNegotiator: rawFromNegotiator, ...rest
  } = qs.parse(search) as Record<string, string>;
  const defaultRedirect = `/?${qs.stringify(rest)}`;

  const queryDocument = rawDocument.replace(/[.,\-_\s]/gi, ''); // cleanup
  const [ requestState, setRequestState ] = React.useState({
    document: '',
    loggedOut: false,
  }); // only do one request for this document

  React.useEffect(() => {
    if (loading) return; // if hasn't finished loading the first profile, return

    if (adminToken) setAdminInfo({ token: adminToken }); // save admin token in local storage

    if (adminToken && outDebtId) {
      initialRoute.set(`/portal/deuda/${outDebtId}`);
    }
    if (initialRoute.get() !== '/') {
      initialRoute.clear();
    }
    const sameBrand = !queryBrand || session.brand === parseBrandString(queryBrand);
    const sameBank = !queryBank || session.bank === parseBankString(queryBank);

    // if `loadedDocument`` is the same as `queryDocument`, there is no need for logout
    // nor auth using `simple` method, just redirect to `initialRoute`
    if (sameBrand
      && sameBank
      && loadedDocument === queryDocument && !requestState.document) {
      setRequestState((prev) => ({ ...prev, loggedOut: true, document: queryDocument }));
    } else if (!requestState.loggedOut) {
      // auto logout once without reload, to ensure no data sharing between users
      logout(true).then(() => setRequestState((prev) => ({ ...prev, loggedOut: true })));
    }

    // if loaded, query and request documents are the same, we are done here, redirect
    if (loadedDocument === queryDocument && requestState.document === queryDocument) {
      eventsTracking.account.simple({ id: userId!, document: loadedDocument }, () => {
        const url = new URL(window.location.origin + initialRoute.get());
        // removes token and id from query string if present
        // this is made after simple/deep-link auth or if already logged in
        if (session.identityValidated) {
          url.searchParams.set('token', 'undefined');
          url.searchParams.set('id', 'undefined');
        }
        const href = url.href.substring(url.origin.length);
        history.push(loadedDocument ? href : defaultRedirect);
      });
      return;
    }

    // only if already loggedOut and hasn't requested this document, do simple auth
    if (requestState.loggedOut && requestState.document !== queryDocument) {
      setRequestState((prev) => ({ ...prev, document: queryDocument }));
      simple(queryDocument, false, adminToken)
        .then(() => refetch())
        .catch(() => history.push(defaultRedirect));
    }
  }, [ loading, queryDocument, history,
    loadedDocument, refetch, userId,
    adminToken, queryBrand, setAdminInfo,
    logout, defaultRedirect, requestState.loggedOut,
    requestState.document, outDebtId, adminInfo.token, queryBank ]);

  const [ localFromNegotiator, setLocalFromNegotiator ] = useLocalFromNegotiator();
  const fromNegotiator = rawFromNegotiator === 'true';
  if (localFromNegotiator !== fromNegotiator) {
    setLocalFromNegotiator(fromNegotiator);
  }

  return (
    <IconDialog icon="loading" rotating open>
      <Typography variant="h3" align="center">{loadingMessage}</Typography>
    </IconDialog>
  );
};

export default Simple;
